/** Element position/size as percentages (0-100) of the card canvas. */
export interface ElementFrame {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type SlideElementId = 'background' | 'logo' | 'title' | 'highlights' | 'image';

export type FrameElementId = Exclude<SlideElementId, 'background'>;

export type SlideBackground = 'imageRight' | 'imageLeft' | 'plain';

export interface SlideImageStyle {
  /** Corner radius in pixels (0 = square corners). */
  borderRadius?: number;
}

export interface SlideComponentStyle {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  borderRadius?: number;
  opacity?: number;
}

export type SlideComponentType =
  | 'rectangle'
  | 'ellipse'
  | 'line'
  | 'image'
  | 'text'
  | 'table'
  | 'chart';

export type SlideChartType = 'bar' | 'line' | 'pie';

export interface SlideChartData {
  chartType: SlideChartType;
  title?: string;
  labels: string[];
  values: number[];
}

export interface SlideChartStyle {
  primaryColor?: string;
  gridColor?: string;
  labelColor?: string;
  colors?: string[];
  showGrid?: boolean;
  showValues?: boolean;
}

export interface SlideTableData {
  headers: string[];
  rows: string[][];
}

export interface SlideTableStyle {
  headerFill?: string;
  headerText?: string;
  cellFill?: string;
  cellText?: string;
  borderColor?: string;
}

export interface SlideComponent {
  id: string;
  type: SlideComponentType;
  frame: ElementFrame;
  text?: string;
  imageUrl?: string;
  imageAlt?: string;
  table?: SlideTableData;
  tableStyle?: SlideTableStyle;
  chart?: SlideChartData;
  chartStyle?: SlideChartStyle;
  style?: SlideComponentStyle;
}

export interface SlideLayout {
  logo?: ElementFrame;
  title?: ElementFrame;
  highlights?: ElementFrame;
  image?: ElementFrame;
  background?: SlideBackground;
  imageStyle?: SlideImageStyle;
  components?: SlideComponent[];
  /** Back-to-front stacking: built-in ids (logo, title, …) and component ids (cmp_…). */
  layerOrder?: string[];
}

export interface PitchSlide {
  id: string;
  title: string;
  highlights: string[];
  imageUrl: string;
  imageAlt: string;
  tabLabel: string;
  layout?: SlideLayout;
}

export const pitchSlides: PitchSlide[] = [
  {
    id: 'intro',
    title: 'Interactive learning for SHS students',
    highlights: [
      'We are personalizing learning so that it is engaging, exciting and fun ',
      'Enhancing the creator economy for educational content creators',
      'Syllabus-aligned video courses for Ghana and Africa',
      'Interactive animations embedded in every lesson',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80',
    imageAlt: 'Students learning together with laptops',
    tabLabel: 'INTRO',
  },
  {
    id: 'problem',
    title: 'Passive learning fails students',
    highlights: [
      'Learning materials have stayed in the past, in this digital and AI age ',
      'Students cannot choose tutors who match how they learn',
      'Static illustrations and plain video lose attention quickly',
      'Extra classes are often needed to close learning gaps',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1580582932707-520aed237b7d?w=800&q=80',
    imageAlt: 'Traditional classroom setting',
    tabLabel: 'PROBLEM',
  },
  {
    id: 'solution',
    title: 'Video + interactive visual content',
    highlights: [
      'On-demand lessons with embedded interactives',
      'Simulations students explore and control',
      'Modular content aligned to the SHS syllabus',
      'Classroom-ready tools that save teachers time',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&q=80',
    imageAlt: 'Collaborative learning with technology',
    tabLabel: 'SOLUTION',
  },
  {
    id: 'validation',
    title: 'Demand is already proven',
    highlights: [
      'E-learning growing fast across Africa',
      'Pilot markets: Ghana and Nigeria',
      'Brilliant.org: 10M+ learners globally',
      'uLesson and Wolo Africa show regional traction',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80',
    imageAlt: 'Team planning in a modern office',
    tabLabel: 'VALIDATION',
  },
  {
    id: 'market',
    title: 'A large, growing market',
    highlights: [
      'Africa e-learning: +US$5.2B by 2028',
      'TAM: US$2.8B · SAM: US$1.8B',
      'Initial target: 50% of SAM',
      'Focused rollout in Ghana and Nigeria',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1454165804604-c07d5a6c2a88?w=800&q=80',
    imageAlt: 'Business growth and market analysis',
    tabLabel: 'MARKET',
  },
  {
    id: 'model',
    title: 'Simple, scalable revenue',
    highlights: [
      'Students & parents: US$5–7 per course',
      'Schools: from US$250/month',
      'Tutors earn 10% per enrollment',
      'Studios in Accra for content production',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80',
    imageAlt: 'Financial planning and business model',
    tabLabel: 'MODEL',
  },
  {
    id: 'edge',
    title: 'Built for African SHS curriculum',
    highlights: [
      'Competitors lack SHS syllabus alignment',
      'Full student control over interactive moments',
      'Clean UX with AI-assisted content tools',
      'Comprehension over exam cramming',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80',
    imageAlt: 'Person using a laptop for interactive learning',
    tabLabel: 'EDGE',
  },
  {
    id: 'gtm',
    title: 'Ghana first, then scale',
    highlights: [
      'Social: Facebook, Instagram, TikTok',
      'SEO, search ads, and retargeting',
      '3-month goal: 20K visits · 10K sign-ups/mo',
      'Telco data bundles for mobile learners',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=800&q=80',
    imageAlt: 'Social media and digital marketing',
    tabLabel: 'GTM',
  },
  {
    id: 'team',
    title: 'Team ready to build',
    highlights: [
      'Founder: 11 yrs software engineering',
      'MBA, The Power Business School',
      'Platform, API, and product in production',
      'Hiring: Content, Product, Marketing leads',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
    imageAlt: 'Founding team collaborating',
    tabLabel: 'TEAM',
  },
  {
    id: 'traction',
    title: 'Platform is live today',
    highlights: [
      'Student & tutor registration',
      'Video lessons, interactives, and payments',
      'Enrollment and progress tracking',
      'Admin studio for course management',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    imageAlt: 'Analytics dashboard showing growth metrics',
    tabLabel: 'TRACTION',
  },
  {
    id: 'ask',
    title: 'Raising US$500K pre-seed',
    highlights: [
      'Goal: 200K enrollments · 500 schools',
      'Hire engineering, product, and content teams',
      'Offices & studios in Accra and Abuja',
      'Scale the minimum lovable product',
    ],
    imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
    imageAlt: 'Team aligned on growth strategy',
    tabLabel: 'ASK',
  },
];
